import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn, formatCurrency } from "@/lib/utils";
import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  EditIcon,
  Coins,
  Loader2,
  LineChart as LineChartIcon,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  AreaChart,
  Area,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { startOfYear, subMonths, subYears, format as formatDate } from "date-fns";

import { usePortfolioItems } from "@/hooks/use-portfolio-items";
import { usePortfolioHistory } from "@/hooks/use-portfolio-history";
import { useMetadata } from "@/hooks/use-metadata";
import { useAccountList } from "@/hooks/use-account-list";
import type { PortfolioItem } from "@/types/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { AggregatedPortfolioHistory } from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  parseDecimal,
  getDisplayData,
  buildGroupColorMap,
  getGroupIcon,
  buildPortfolioChartSeries,
} from "@/lib/portfolio-utils";
import { translateCategoryGroup } from "@/lib/category-i18n";
import { IncomeTermsModal } from "@/components/income/income-terms-modal";
import { canHoldIncomeTerms } from "@/lib/passive-income";
import { DataUpdatingBanner } from '@/components/processing/DataUpdatingBanner';
import { HoldingPicker } from "@/components/portfolio/holding-picker";
import { CopyTradeBadge } from "@/components/etoro/copy-trade-badge";
import { useToast } from "@/hooks/use-toast";
import {
  buildHoldingOptions,
  buildHoldingSeries,
  holdingKey,
  parseHoldingParam,
  seriesChangePercent,
  toHistoryScope,
} from "@/lib/portfolio-holding";

// ── Symbol-level merge (cross-account deduplication) ──────────────────────
//
// When the user selects "All accounts", the API returns one PortfolioItem per
// (symbol, accountId) pair. For display purposes we want a single row per
// symbol. This helper merges all items that share a symbol by summing their
// financial summaries and marking the merged item as account-agnostic.
//
// Rules:
//  - quantity          → summed
//  - costBasis         → summed
//  - marketValue       → summed
//  - unrealizedPnL     → summed; unrealizedPnLPercent recalculated
//  - realizedPnL       → summed
//  - totalInvested     → summed
//  - hasLotMismatch    → OR (any account's mismatch surfaces on the merged row)
//  - accountId         → null  (merged, not tied to one account)
//  - id                → first item's id (for React keys; doesn't drive any query)
//  - mergedItemIds     → every merged item's id (the "Show in chart" action, #131)
//  - Everything else   → first item (category, currency, symbol are identical)

/** A table row: a PortfolioItem, or several merged ones (`mergedItemIds`). */
type PortfolioRowItem = PortfolioItem & { mergedItemIds?: number[] };

function mergeFinancialSummary(
  items: PortfolioItem[],
  getBlock: (item: PortfolioItem) => PortfolioItem["native"],
): PortfolioItem["native"] {
  const summed = items.reduce(
    (acc, item) => {
      const b = getBlock(item);
      if (!b) return acc;
      return {
        costBasis: acc.costBasis + parseDecimal(b.costBasis),
        marketValue: acc.marketValue + parseDecimal(b.marketValue),
        unrealizedPnL: acc.unrealizedPnL + parseDecimal(b.unrealizedPnL),
        unrealizedPnLPercent: 0, // recalculated below
        realizedPnL: acc.realizedPnL + parseDecimal(b.realizedPnL),
        totalInvested: acc.totalInvested + parseDecimal(b.totalInvested),
      };
    },
    { costBasis: 0, marketValue: 0, unrealizedPnL: 0, unrealizedPnLPercent: 0, realizedPnL: 0, totalInvested: 0 },
  );
  summed.unrealizedPnLPercent =
    summed.costBasis !== 0 ? (summed.unrealizedPnL / summed.costBasis) * 100 : 0;
  return summed;
}

function mergePortfolioItems(items: PortfolioItem[]): PortfolioRowItem {
  if (items.length === 1) return items[0];
  const base = items[0];
  const quantity = items.reduce((s, i) => s + parseDecimal(i.quantity), 0);
  const hasLotMismatch = items.some((i) => i.hasLotMismatch);
  const hasPortfolio = items.some((i) => i.portfolio != null);
  return {
    ...base,
    accountId: null,
    mergedItemIds: items.map((i) => i.id),
    quantity,
    hasLotMismatch,
    native: mergeFinancialSummary(items, (i) => i.native),
    usd: mergeFinancialSummary(items, (i) => i.usd),
    ...(hasPortfolio && {
      portfolio: mergeFinancialSummary(items, (i) => i.portfolio ?? i.usd),
    }),
  };
}

// ── Constants ──────────────────────────────────────────────────────────────

const EMPTY_ARRAY: [] = [];

const TIME_RANGES = [
  { value: "1m", label: "1M" },
  { value: "6m", label: "6M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "ALL" },
] as const;

// ── Sort Chevron ───────────────────────────────────────────────────────────

function SortChevron({ dir }: { dir: "asc" | "desc" | "none" }) {
  return (
    <span className="inline-flex flex-col gap-px ml-1 align-middle" style={{ opacity: dir === "none" ? 0.38 : 1 }}>
      <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
        <path d="M1 4L4 1L7 4" stroke={dir === "asc" ? "hsl(var(--foreground))" : "hsl(var(--muted-fg))"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
        <path d="M1 1L4 4L7 1" stroke={dir === "desc" ? "hsl(var(--foreground))" : "hsl(var(--muted-fg))"} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// ── Asset Row ──────────────────────────────────────────────────────────────

// Passive Income (#77): opens the Income Terms modal in place (unlike the
// debt row, which navigates away). Shown on every income-capable holding.
function IncomeTermsButton({ item, onOpen }: { item: PortfolioItem; onOpen?: (item: PortfolioItem) => void }) {
  const { t } = useTranslation();
  if (!onOpen || !canHoldIncomeTerms(item.category)) return null;
  const configured = Boolean(item.incomeTerms);
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "h-6 w-6 p-0 ml-1 align-middle",
        configured ? "text-brand-primary" : "text-muted-foreground hover:text-brand-deep",
      )}
      aria-label={t("incomeTerms.action")}
      title={configured ? t("incomeTerms.actionConfigured") : t("incomeTerms.action")}
      onClick={() => onOpen(item)}
      data-testid={`income-terms-${item.id}`}
    >
      <Coins className="h-3.5 w-3.5" />
    </Button>
  );
}

// Holding filter (#131): scopes the performance chart to this row's holding.
// Hidden on a merged row of several manual items — the picker lists those
// separately (id-keyed), so no single chart entry matches the row.
function ShowInChartButton({ item, onShow }: { item: PortfolioRowItem; onShow?: (item: PortfolioItem) => void }) {
  const { t } = useTranslation();
  if (!onShow) return null;
  if (item.source === "MANUAL" && (item.mergedItemIds?.length ?? 1) > 1) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 ml-1 align-middle text-muted-foreground hover:text-brand-deep"
      aria-label={t("portfolio.showInChart")}
      title={t("portfolio.showInChart")}
      onClick={() => onShow(item)}
      data-testid={`show-in-chart-${item.id}`}
    >
      <LineChartIcon className="h-3.5 w-3.5" />
    </Button>
  );
}

function AssetRow({ item, currency, onIncomeTerms, onShowInChart }: { item: PortfolioRowItem; currency: string; onIncomeTerms?: (item: PortfolioItem) => void; onShowInChart?: (item: PortfolioItem) => void }) {
  const { t } = useTranslation();
  const data = getDisplayData(item, currency);
  const marketValue = parseDecimal(data.marketValue);

  if (item.category.group === "Cash") {
    return (
      <TableRow className="hover:bg-accent/30">
        <TableCell className="font-medium">
          {item.symbol}
          <IncomeTermsButton item={item} onOpen={onIncomeTerms} />
          <ShowInChartButton item={item} onShow={onShowInChart} />
        </TableCell>
        <TableCell className="hidden md:table-cell" />
        <TableCell className="hidden md:table-cell" />
        <TableCell className="hidden md:table-cell" />
        <TableCell className="hidden sm:table-cell" />
        <TableCell className="hidden md:table-cell" />
        <TableCell className="hidden sm:table-cell" />
        <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(marketValue, currency)}</TableCell>
      </TableRow>
    );
  }

  const quantity = parseDecimal(item.quantity);
  const costBasis = parseDecimal(data.costBasis);
  const unrealizedPnL = parseDecimal(data.unrealizedPnL);
  const realizedPnL = parseDecimal(data.realizedPnL);
  const totalInvested = parseDecimal(data.totalInvested);
  const price = quantity > 0 ? marketValue / quantity : 0;
  const totalPnL = realizedPnL + unrealizedPnL;
  const roiPercent = totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0;
  const isClosed = quantity === 0;

  return (
    <TableRow className={`hover:bg-accent/30 ${isClosed ? "opacity-60" : ""}`}>
      <TableCell className="font-medium">
        <div className="flex items-center">
          <span>{item.symbol}</span>
          {item.category.processingHint === "COPY_TRADING" && <CopyTradeBadge copyTrade={item.copyTrade} className="ml-1.5" />}
          <IncomeTermsButton item={item} onOpen={isClosed ? undefined : onIncomeTerms} />
          <ShowInChartButton item={item} onShow={onShowInChart} />
        </div>
        <p className="text-xs text-muted-foreground sm:hidden">
          {unrealizedPnL !== 0 && (
            <span className={unrealizedPnL >= 0 ? "text-positive" : "text-negative"}>
              {formatCurrency(unrealizedPnL, currency)}
            </span>
          )}
          {unrealizedPnL !== 0 && totalInvested > 0 && " · "}
          {totalInvested > 0 && (
            <span className={roiPercent >= 0 ? "text-positive" : "text-negative"}>
              {roiPercent >= 0 ? "+" : ""}{roiPercent.toFixed(1)}%
            </span>
          )}
        </p>
      </TableCell>
      <TableCell className="hidden md:table-cell tabular-nums">{quantity.toFixed(2)}</TableCell>
      <TableCell className="hidden md:table-cell tabular-nums">{formatCurrency(price, currency)}</TableCell>
      <TableCell className="hidden md:table-cell tabular-nums">{formatCurrency(costBasis, currency)}</TableCell>
      <TableCell className={`hidden sm:table-cell tabular-nums ${unrealizedPnL >= 0 ? "text-positive" : "text-negative"}`}>
        {formatCurrency(unrealizedPnL, currency)}
      </TableCell>
      <TableCell className={`hidden md:table-cell tabular-nums ${realizedPnL >= 0 ? "text-positive" : "text-negative"}`}>
        {formatCurrency(realizedPnL, currency)}
      </TableCell>
      <TableCell className="text-right hidden sm:table-cell">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tabular-nums ${
                roiPercent >= 0
                  ? "bg-positive/10 text-positive border border-positive/20"
                  : "bg-negative/10 text-negative border border-negative/20"
              }`}>
                {totalInvested > 0 ? `${roiPercent >= 0 ? "+" : ""}${roiPercent.toFixed(2)}%` : "N/A"}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("portfolio.roiTooltip")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(marketValue, currency)}</TableCell>
    </TableRow>
  );
}

// ── Liability Row ──────────────────────────────────────────────────────────

function LiabilityRow({ item, currency, onShowInChart }: { item: PortfolioRowItem; currency: string; onShowInChart?: (item: PortfolioItem) => void }) {
  const { t } = useTranslation();
  const data = getDisplayData(item, currency);
  const marketValue = Math.abs(parseDecimal(data.marketValue));
  const navigate = useNavigate();

  return (
    <TableRow className="hover:bg-accent/30">
      <TableCell className="font-medium">
        <div className="flex items-center">
          <span>{item.symbol}</span>
          <ShowInChartButton item={item} onShow={onShowInChart} />
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell tabular-nums">
        {item.debtTerms?.interestRate != null ? `${item.debtTerms.interestRate}%` : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="hidden sm:table-cell tabular-nums">
        {item.debtTerms?.termInMonths != null ? `${item.debtTerms.termInMonths} mo` : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-right font-semibold tabular-nums text-negative">
        {formatCurrency(marketValue, currency)}
      </TableCell>
      <TableCell className="text-right">
        {/* Icon-only on phones so the table fits 390 px (#155). */}
        <Button
          variant="outline"
          size="sm"
          className="h-9 w-9 p-0 sm:h-8 sm:w-auto sm:px-3 gap-1.5 text-xs"
          onClick={() => navigate(`/assets?item=${item.id}&modal=debt`)}
          aria-label={item.debtTerms ? t("portfolio.editTerms") : t("portfolio.addTerms")}
        >
          <EditIcon className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{item.debtTerms ? t("portfolio.editTerms") : t("portfolio.addTerms")}</span>
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ── Main Page Component ────────────────────────────────────────────────────

export default function PortfolioHoldingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const chartCardRef = useRef<HTMLDivElement>(null);
  const userLocale = i18n.language || window.navigator.language || "en-US";

  // ── State ──
  const [sortField, setSortField] = useState("marketValue");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [closedSectionsVisible, setClosedSectionsVisible] = useState<Record<string, boolean>>({});
  const [timeRange, setTimeRange] = useState("all");
  const [showDebt, setShowDebt] = useState(false);
  const [incomeTermsItem, setIncomeTermsItem] = useState<PortfolioItem | null>(null);
  // Account and country filters — "all" means no filter applied
  const [selectedAccountId, setSelectedAccountId] = useState<string>("all");
  const [selectedCountryId, setSelectedCountryId] = useState<string>("all");

  // ── Data Fetching ──
  const { data: metadata, isLoading: metadataLoading, error: metadataError } = useMetadata();
  const { accounts } = useAccountList();

  // Derive distinct countries from the account list for the country filter
  const distinctCountries = useMemo(() => {
    const seen = new Set<string>();
    return accounts
      .filter((a) => { if (seen.has(a.countryId)) return false; seen.add(a.countryId); return true; })
      .map((a) => ({ countryId: a.countryId, label: a.countryId }));
  }, [accounts]);

  const portfolioFilters = useMemo(() => ({
    ...(selectedAccountId !== "all" && { accountId: parseInt(selectedAccountId, 10) }),
    ...(selectedCountryId !== "all" && { countryId: selectedCountryId }),
  }), [selectedAccountId, selectedCountryId]);

  const { data: portfolioData, isLoading: itemsLoading, isFetching: itemsFetching, error: itemsError } = usePortfolioItems(portfolioFilters);
  const portfolioItems = useMemo(() => portfolioData?.items ?? [], [portfolioData?.items]);
  const portfolioCurrency = portfolioData?.portfolioCurrency ?? "USD";
  // Cached values are on screen and a background refetch is running.
  const isRefreshing = itemsFetching && !itemsLoading;

  const historyDateFilter = useMemo(() => {
    const now = new Date();
    switch (timeRange) {
      case "1m": return { from: subMonths(now, 1).toISOString() };
      case "6m": return { from: subMonths(now, 6).toISOString() };
      case "1y": return { from: subYears(now, 1).toISOString() };
      case "ytd": return { from: startOfYear(now).toISOString() };
      default: return {};
    }
  }, [timeRange]);

  // ── Holding filter (#131) ──
  // The URL (`?holding=AAPL` or `?holding=item:<id>`) is the only source of
  // truth for the selection. It resolves against the loaded items, so an
  // unknown value never reaches the API.
  const holdingParam = parseHoldingParam(searchParams.get("holding"));

  const holdingOptions = useMemo(
    () => buildHoldingOptions(portfolioItems, { portfolioCurrency, showDebt }),
    [portfolioItems, portfolioCurrency, showDebt],
  );

  const selectedHolding = useMemo(
    () => (holdingParam && !itemsLoading ? holdingOptions.find((o) => o.key === holdingParam) ?? null : null),
    [holdingParam, itemsLoading, holdingOptions],
  );

  const setHoldingParam = useCallback((key: string | null, replace = false) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key) next.set("holding", key);
      else next.delete("holding");
      return next;
    }, { replace });
  }, [setSearchParams]);

  // Set when the account changes with a holding selected, so the strip below
  // can tell the user why the selection went away.
  const pendingAccountSwitch = useRef<{ key: string; symbol: string; accountName: string } | null>(null);

  const handleAccountChange = (value: string) => {
    if (selectedHolding) {
      const account = accounts.find((a) => String(a.id) === value);
      pendingAccountSwitch.current = {
        key: selectedHolding.key,
        symbol: selectedHolding.symbol,
        accountName: account?.accountName ?? t("portfolio.allAccounts"),
      };
    }
    setSelectedAccountId(value);
  };

  // Drop a selection that doesn't resolve once the items have settled: an
  // unknown deep link (silently), a holding the newly selected account doesn't
  // hold (with a notice), or a debt holding after "Show debt" is turned off.
  useEffect(() => {
    if (itemsLoading || itemsFetching) return;
    const pending = pendingAccountSwitch.current;
    pendingAccountSwitch.current = null;
    if (!holdingParam || selectedHolding) return;
    setHoldingParam(null, true);
    if (pending && pending.key === holdingParam) {
      toast({
        description: t("portfolio.holdingNotInAccount", { symbol: pending.symbol, account: pending.accountName }),
      });
    }
  }, [holdingParam, selectedHolding, itemsLoading, itemsFetching, setHoldingParam, toast, t]);

  const historyEnabled = !holdingParam || selectedHolding !== null;
  const { data: historyResponse, isLoading: historyQueryLoading, error: historyError } = usePortfolioHistory({
    ...historyDateFilter,
    type: showDebt ? "Investments,Debt,Asset" : "Investments,Asset",
    // Scope the holdings graph to the selected account, matching the table filter.
    ...(selectedAccountId !== "all" && { accountId: parseInt(selectedAccountId, 10) }),
    ...(selectedHolding && toHistoryScope(selectedHolding.key)),
  }, { enabled: historyEnabled });
  // A deep link waits for the items before the (scoped) history is requested.
  const historyLoading = historyQueryLoading || !historyEnabled;
  const historyData = useMemo(() => historyResponse?.history ?? [], [historyResponse?.history]);
  const categories = metadata?.categories ?? EMPTY_ARRAY;

  // ── Data Processing ──

  const { assetList, liabilityList } = useMemo(() => {
    const rawAssets: PortfolioItem[] = [];
    const rawLiabilities: PortfolioItem[] = [];
    portfolioItems.forEach((item) => {
      if (item.category.type === "Debt") {
        rawLiabilities.push(item);
      } else {
        rawAssets.push(item);
      }
    });

    // When viewing all accounts, merge same-symbol items into one row per symbol.
    // A specific account filter is already applied server-side, so no merge is needed there.
    if (selectedAccountId !== "all") {
      return { assetList: rawAssets, liabilityList: rawLiabilities };
    }

    const mergeBySymbol = (items: PortfolioItem[]): PortfolioItem[] => {
      const bySymbol = new Map<string, PortfolioItem[]>();
      for (const item of items) {
        const key = item.symbol;
        if (!bySymbol.has(key)) bySymbol.set(key, []);
        bySymbol.get(key)!.push(item);
      }
      return Array.from(bySymbol.values()).map(mergePortfolioItems);
    };

    return {
      assetList: mergeBySymbol(rawAssets),
      liabilityList: mergeBySymbol(rawLiabilities),
    };
  }, [portfolioItems, selectedAccountId]);

  // Cash is part of net worth (#144); only P&L below leaves it out.
  const totalAssetsValue = useMemo(
    () => assetList
      .reduce((sum, item) => sum + parseDecimal(getDisplayData(item, portfolioCurrency).marketValue), 0),
    [assetList, portfolioCurrency]
  );

  const totalLiabilitiesValue = useMemo(
    () => liabilityList.reduce((sum, item) => sum + Math.abs(parseDecimal(getDisplayData(item, portfolioCurrency).marketValue)), 0),
    [liabilityList, portfolioCurrency]
  );

  const netWorth = totalAssetsValue - totalLiabilitiesValue;

  const totalPnL = useMemo(() => {
    return assetList
      .filter((item) => item.category.group !== "Cash")
      .reduce((sum, item) => {
        const d = getDisplayData(item, portfolioCurrency);
        return sum + parseDecimal(d.unrealizedPnL) + parseDecimal(d.realizedPnL);
      }, 0);
  }, [assetList, portfolioCurrency]);

  // Sorted & grouped assets
  const sortedAssets = useMemo(() => {
    return [...assetList].sort((a, b) => {
      const aData = getDisplayData(a, portfolioCurrency);
      const bData = getDisplayData(b, portfolioCurrency);
      const aVal = parseDecimal(aData[sortField as keyof typeof aData]);
      const bVal = parseDecimal(bData[sortField as keyof typeof bData]);
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [assetList, sortField, sortOrder, portfolioCurrency]);

  const sortedLiabilities = useMemo(() => {
    return [...liabilityList].sort((a, b) => {
      const aVal = Math.abs(parseDecimal(getDisplayData(a, portfolioCurrency).marketValue));
      const bVal = Math.abs(parseDecimal(getDisplayData(b, portfolioCurrency).marketValue));
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [liabilityList, sortOrder, portfolioCurrency]);

  const groupedAssets = useMemo(() => {
    // Build groups from sorted assets
    const groups = sortedAssets.reduce((acc, asset) => {
      const group = asset.category.group;
      if (!acc[group]) acc[group] = [];
      acc[group].push(asset);
      return acc;
    }, {} as Record<string, PortfolioItem[]>);

    // Sort entries by group total (descending), with Cash always last
    const entries = Object.entries(groups).sort(([groupA, itemsA], [groupB, itemsB]) => {
      const aIsCash = groupA === "Cash";
      const bIsCash = groupB === "Cash";
      if (aIsCash !== bIsCash) return aIsCash ? 1 : -1;
      const totalA = itemsA.reduce((s, i) => s + parseDecimal(getDisplayData(i, portfolioCurrency).marketValue), 0);
      const totalB = itemsB.reduce((s, i) => s + parseDecimal(getDisplayData(i, portfolioCurrency).marketValue), 0);
      return totalB - totalA;
    });

    return Object.fromEntries(entries);
  }, [sortedAssets, portfolioCurrency]);

  // ── Chart Data ──

  const { chartData, allGroups, debtGroups } = useMemo(
    () => buildPortfolioChartSeries(historyData as AggregatedPortfolioHistory[], { showDebt }),
    [historyData, showDebt],
  );

  // Holding mode (#131): one series replaces the stacked chart. No ramp-up zero
  // before the first valuation; a closed position steps to zero after it closed.
  const holdingSeries = useMemo(
    () => (selectedHolding ? buildHoldingSeries(historyData, { isClosed: selectedHolding.isClosed }) : []),
    [historyData, selectedHolding],
  );

  const { performanceSinceStartPercent } = useMemo(() => {
    if (selectedHolding) return { performanceSinceStartPercent: seriesChangePercent(holdingSeries) };
    if (!chartData || chartData.length === 0) return { performanceSinceStartPercent: 0 };
    const firstValue = chartData[0]["Net Worth"] as number;
    const lastValue = chartData[chartData.length - 1]["Net Worth"] as number;
    const performance = lastValue - firstValue;
    return {
      performanceSinceStartPercent: firstValue === 0 ? 0 : (performance / firstValue) * 100,
    };
  }, [chartData, selectedHolding, holdingSeries]);

  // The headline is the selected holding's table value (merged across accounts
  // like the table row), mirroring how the default headline uses netWorth.
  const headlineValue = selectedHolding
    ? (selectedHolding.isDebt ? -selectedHolding.value : selectedHolding.value)
    : netWorth;

  // ── Dynamic color map ──

  const groupColorMap = useMemo(
    () => buildGroupColorMap(allGroups, debtGroups),
    [allGroups, debtGroups]
  );

  const getColor = (group: string) => groupColorMap[group] || "#9A95A4";

  // The holding series takes its category group's colour (debt family for debt),
  // assigned over every group the user holds so it doesn't depend on the scope.
  const holdingColor = useMemo(() => {
    if (!selectedHolding) return undefined;
    const assetGroups = new Set<string>();
    const debtGroupSet = new Set<string>();
    for (const o of holdingOptions) (o.isDebt ? debtGroupSet : assetGroups).add(o.group);
    return buildGroupColorMap([...assetGroups], debtGroupSet)[selectedHolding.group];
  }, [selectedHolding, holdingOptions]);

  const handleShowInChart = (item: PortfolioItem) => {
    if (item.category.type === "Debt") setShowDebt(true);
    setHoldingParam(holdingKey(item));
    chartCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── Handlers ──

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const dirFor = (field: string): "asc" | "desc" | "none" =>
    sortField === field ? sortOrder : "none";

  const toggleGroup = (group: string) =>
    setExpandedGroups((p) => ({ ...p, [group]: !p[group] }));

  // ── Tooltip ──

  const PerformanceChartTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { name: string; color: string; value: number }[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;

    if (selectedHolding) {
      const point = payload[0];
      return (
        <div className="p-3 bg-background/95 backdrop-blur-sm border rounded-xl shadow-lg min-w-[180px]">
          <p className="font-semibold text-sm mb-2">{label ? formatDate(new Date(label), "PPP") : ""}</p>
          <div className="flex justify-between items-center gap-6 text-sm">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: point.color }} />
              {selectedHolding.symbol}
            </span>
            <span className="tabular-nums">{formatCurrency(point.value, portfolioCurrency)}</span>
          </div>
        </div>
      );
    }

    const netWorthLabel = t("portfolio.netWorth");
    const netWorthItem = payload.find((item) => item.name === netWorthLabel || item.name === "Net Worth");
    const groupItems = payload
      .filter((item) => item.name !== netWorthLabel && item.name !== "Net Worth" && item.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const netWorthValue = netWorthItem?.value ?? groupItems.reduce((sum, item) => sum + item.value, 0);

    return (
      <div className="p-3 bg-background/95 backdrop-blur-sm border rounded-xl shadow-lg min-w-[210px]">
        <p className="font-semibold text-sm mb-2">{label ? formatDate(new Date(label), "PPP") : ""}</p>
        <div className="space-y-1 mb-2">
          {groupItems.map((item) => (
            <div key={item.name} className="flex justify-between items-center gap-6 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                {translateCategoryGroup(t, item.name)}
              </span>
              <span className="tabular-nums">{formatCurrency(item.value, portfolioCurrency)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-2 border-t font-semibold text-sm">
          <span>{t("portfolio.netWorth")}</span>
          <span className={`tabular-nums ${netWorthValue >= 0 ? "text-positive" : "text-negative"}`}>
            {formatCurrency(netWorthValue, portfolioCurrency)}
          </span>
        </div>
      </div>
    );
  };

  // ── Loading & Error ──

  const isLoading = itemsLoading || metadataLoading;
  const hasError = itemsError || historyError || metadataError;

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <HelpCircle className="h-4 w-4" />
          <AlertTitle>{t("portfolio.errorTitle")}</AlertTitle>
          <AlertDescription>
            {t("portfolio.errorDescription")}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (portfolioItems.length === 0) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight mb-2">{t("portfolio.title")}</h2>
          <p className="text-muted-foreground">{t("portfolio.subtitle")}</p>
        </div>
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground mb-4">{t("portfolio.emptyState")}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate("/accounts")}>{t("portfolio.connectAccount")}</Button>
              <Button variant="outline" onClick={() => navigate("/imports")}>{t("portfolio.importCSV")}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Render: Sortable Header Helper ──

  const SortableHeader = ({
    field,
    children,
    align = "left",
    className: extraClassName,
  }: {
    field: string;
    children: React.ReactNode;
    align?: "left" | "right";
    className?: string;
  }) => (
    <TableHead
      className={cn(`cursor-pointer select-none ${align === "right" ? "text-right" : ""}`, extraClassName)}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        <SortChevron dir={dirFor(field)} />
      </span>
    </TableHead>
  );

  const totalAssetPositions = assetList.length;
  const totalLiabilityPositions = liabilityList.length;

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col space-y-8">
        {/* Processing status (#100): recalculation in progress / updated X ago. */}
        <DataUpdatingBanner watch={['PORTFOLIO_UPDATE', 'SECURITY_DATA']} invalidate={[['portfolio-holdings'], ['portfolio-history'], ['portfolio-items']]} />
      {/* ── Page Header ── */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight mb-2">{t("portfolio.title")}</h2>
        <p className="text-muted-foreground">{t("portfolio.subtitle")}</p>
      </div>

      {/* ── Performance Chart Card ── */}
      <div ref={chartCardRef} className="scroll-mt-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* KPI Row */}
            <div className="flex items-baseline gap-3">
              <span className="flex items-center gap-2 text-3xl font-bold tabular-nums tracking-tight">
                <span data-testid="portfolio-headline">{formatCurrency(headlineValue, portfolioCurrency)}</span>
                {isRefreshing && (
                  <Loader2
                    className="h-4 w-4 animate-spin text-muted-foreground"
                    aria-label={t("common.refreshing", "Refreshing")}
                  />
                )}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tabular-nums ${
                  performanceSinceStartPercent >= 0
                    ? "bg-positive/10 text-positive border border-positive/20"
                    : "bg-negative/10 text-negative border border-negative/20"
                }`}
              >
                {performanceSinceStartPercent >= 0 ? "+" : ""}
                {performanceSinceStartPercent.toFixed(2)}%
              </span>
            </div>

            {/* Controls Row */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Period Tabs (pill-shaped) */}
              <div className="inline-flex items-center rounded-xl bg-accent p-1 gap-0.5">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setTimeRange(range.value)}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                      timeRange === range.value
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              {/* Account Filter */}
              {accounts.length > 1 && (
                <Select value={selectedAccountId} onValueChange={handleAccountChange}>
                  <SelectTrigger className="h-7 w-40 text-xs">
                    <SelectValue placeholder={t("portfolio.allAccounts")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("portfolio.allAccounts")}</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={String(account.id)}>
                        {account.accountName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Holding Filter (#131) */}
              <HoldingPicker
                options={holdingOptions}
                selected={selectedHolding}
                currency={portfolioCurrency}
                onSelect={(key) => setHoldingParam(key)}
                onClear={() => setHoldingParam(null)}
              />

              {/* Country Filter */}
              {distinctCountries.length > 1 && (
                <Select value={selectedCountryId} onValueChange={setSelectedCountryId}>
                  <SelectTrigger className="h-7 w-36 text-xs">
                    <SelectValue placeholder={t("portfolio.allCountries")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("portfolio.allCountries")}</SelectItem>
                    {distinctCountries.map(({ countryId, label }) => (
                      <SelectItem key={countryId} value={countryId}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Show Debt Toggle */}
              <div className="flex items-center gap-2">
                <Switch id="show-debt" checked={showDebt} onCheckedChange={setShowDebt} />
                <Label htmlFor="show-debt" className="text-xs text-muted-foreground">
                  {t("portfolio.showDebt")}
                </Label>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {historyLoading ? (
            <Skeleton className="h-80 w-full rounded-lg" />
          ) : selectedHolding ? (
            holdingSeries.length === 0 ? (
              <div className="h-80 flex flex-col items-center justify-center gap-1 text-muted-foreground text-sm">
                <span>{t("portfolio.noHistoricalData")}</span>
                <span>{t("portfolio.noHistoryForHolding", { name: selectedHolding.symbol })}</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <AreaChart data={holdingSeries}>
                  <defs>
                    <linearGradient id="color-holding" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={holdingColor ?? getColor(selectedHolding.group)} stopOpacity={0.75} />
                      <stop offset="95%" stopColor={holdingColor ?? getColor(selectedHolding.group)} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={(str) => formatDate(new Date(str), "MMM d")}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-fg))"
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    tickFormatter={(val) => formatCurrency(val, portfolioCurrency, userLocale, { notation: "compact" })}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-fg))"
                  />
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-fg))" strokeWidth={1} />
                  <RechartsTooltip content={<PerformanceChartTooltip />} />
                  <RechartsLegend />
                  <Area
                    type="linear"
                    dataKey="value"
                    stroke={holdingColor ?? getColor(selectedHolding.group)}
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill="url(#color-holding)"
                    name={selectedHolding.symbol}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )
          ) : chartData.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-muted-foreground text-sm">
              {t("portfolio.noHistoricalData")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <AreaChart data={chartData}>
                <defs>
                  {allGroups.map((group) => (
                    <linearGradient key={group} id={`color-${group.replace(/\s+/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={getColor(group)} stopOpacity={0.75} />
                      <stop offset="95%" stopColor={getColor(group)} stopOpacity={0.05} />
                    </linearGradient>
                  ))}
                </defs>
                <XAxis
                  dataKey="date"
                  tickFormatter={(str) => formatDate(new Date(str), "MMM d")}
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-fg))"
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tickFormatter={(val) => formatCurrency(val, portfolioCurrency, userLocale, { notation: "compact" })}
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-fg))"
                />
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <ReferenceLine y={0} stroke="hsl(var(--muted-fg))" strokeWidth={1} />
                <RechartsTooltip content={<PerformanceChartTooltip />} />
                <RechartsLegend />

                {/* Investment asset areas — stacked above zero */}
                {allGroups
                  .filter((g) => !debtGroups.has(g))
                  .map((group) => (
                    <Area
                      key={group}
                      type="linear"
                      dataKey={group}
                      stackId="assets"
                      stroke={getColor(group)}
                      strokeWidth={1}
                      fillOpacity={1}
                      fill={`url(#color-${group.replace(/\s+/g, "")})`}
                      name={group}
                    />
                  ))}

                {/* Debt areas — stack below zero (values are negated in chartData) */}
                {showDebt &&
                  allGroups
                    .filter((g) => debtGroups.has(g))
                    .map((group) => (
                      <Area
                        key={group}
                        type="linear"
                        dataKey={group}
                        stackId="debt"
                        stroke={getColor(group)}
                        strokeWidth={1}
                        fillOpacity={1}
                        fill={`url(#color-${group.replace(/\s+/g, "")})`}
                        name={group}
                      />
                    ))}

                {/* Net Worth — dashed overlay line, not stacked */}
                <Line
                  type="linear"
                  dataKey="Net Worth"
                  stroke="hsl(var(--brand-deep))"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={false}
                  name={t("portfolio.netWorth")}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      </div>

      {/* ── Assets Card ── */}
      {totalAssetPositions > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <CardTitle>{t("portfolio.assets")}</CardTitle>
              <Badge variant="outline" className="text-xs font-medium">
                {t("portfolio.positionCount", { count: totalAssetPositions })}
              </Badge>
              <span className="ml-auto text-lg font-bold tabular-nums tracking-tight">
                {formatCurrency(totalAssetsValue, portfolioCurrency)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {Object.entries(groupedAssets).map(([group, groupItems]) => {
              const isCashGroup = group === "Cash";
              const openPositions = isCashGroup ? groupItems : groupItems.filter((item) => parseDecimal(item.quantity) > 0);
              const closedPositions = isCashGroup ? [] : groupItems.filter((item) => parseDecimal(item.quantity) === 0);
              const isExpanded = expandedGroups[group] === true; // default collapsed
              const groupTotal = groupItems.reduce(
                (s, i) => s + parseDecimal(getDisplayData(i, portfolioCurrency).marketValue),
                0
              );
              const GroupIcon = getGroupIcon(group, groupItems[0]?.category?.processingHint);

              return (
                <div key={group} className="mb-4 last:mb-0">
                  {/* Group Header */}
                  <button
                    className="flex items-center gap-2 w-full py-2 text-left hover:bg-accent/30 rounded-lg px-2 -mx-2 transition-colors"
                    onClick={() => toggleGroup(group)}
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded" style={{ color: getColor(group) }}>
                      <GroupIcon className="h-4 w-4" />
                    </span>
                    <span className="font-medium text-sm">{translateCategoryGroup(t, group)}</span>
                    <Badge variant="secondary" className="text-xs ml-1">
                      {groupItems.length}
                    </Badge>
                    <span className="ml-auto text-sm font-semibold tabular-nums">
                      {formatCurrency(groupTotal, portfolioCurrency)}
                    </span>
                  </button>

                  {isExpanded && (
                    <>
                      <div className="overflow-x-auto mt-1">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-accent/40">
                              <SortableHeader field="symbol">{t("portfolio.symbol")}</SortableHeader>
                              <SortableHeader field="quantity" className="hidden md:table-cell">{t("portfolio.qty")}</SortableHeader>
                              <TableHead className="hidden md:table-cell">{t("portfolio.price")}</TableHead>
                              <SortableHeader field="costBasis" className="hidden md:table-cell">{t("portfolio.costBasis")}</SortableHeader>
                              <SortableHeader field="unrealizedPnL" className="hidden sm:table-cell">{t("portfolio.unrealizedPnL")}</SortableHeader>
                              <SortableHeader field="realizedPnL" className="hidden md:table-cell">{t("portfolio.realizedPnL")}</SortableHeader>
                              <TableHead className="text-right hidden sm:table-cell">{t("portfolio.roiPercent")}</TableHead>
                              <SortableHeader field="marketValue" align="right">{t("portfolio.totalValue")}</SortableHeader>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {openPositions.map((item) => (
                              <AssetRow key={item.id} item={item} currency={portfolioCurrency} onIncomeTerms={setIncomeTermsItem} onShowInChart={handleShowInChart} />
                            ))}
                            {closedSectionsVisible[group] &&
                              closedPositions.map((item) => (
                                <AssetRow key={item.id} item={item} currency={portfolioCurrency} onIncomeTerms={setIncomeTermsItem} onShowInChart={handleShowInChart} />
                              ))}
                          </TableBody>
                        </Table>
                      </div>
                      {closedPositions.length > 0 && (
                        <div className="text-center mt-2">
                          <Button
                            variant="link"
                            size="sm"
                            className="text-xs"
                            onClick={() => setClosedSectionsVisible((p) => ({ ...p, [group]: !p[group] }))}
                          >
                            {closedSectionsVisible[group]
                              ? t("portfolio.hideClosedPositions", { count: closedPositions.length })
                              : t("portfolio.showClosedPositions", { count: closedPositions.length })}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* ── Liabilities Card ── */}
      {totalLiabilityPositions > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <CardTitle>{t("portfolio.liabilities")}</CardTitle>
              <Badge variant="outline" className="text-xs font-medium">
                {totalLiabilityPositions}
              </Badge>
              <span className="ml-auto text-lg font-bold tabular-nums tracking-tight text-negative">
                {formatCurrency(totalLiabilitiesValue, portfolioCurrency)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-accent/40">
                    <TableHead>{t("portfolio.liability")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("portfolio.interestRate")}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t("portfolio.term")}</TableHead>
                    <TableHead className="text-right">{t("portfolio.balance")}</TableHead>
                    <TableHead className="text-right"><span className="sr-only sm:not-sr-only">{t("common.actions")}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedLiabilities.map((item) => (
                    <LiabilityRow key={item.id} item={item} currency={portfolioCurrency} onShowInChart={handleShowInChart} />
                  ))}
                </TableBody>
                {sortedLiabilities.length > 1 && (
                  <tfoot>
                    <tr className="border-t">
                      <td className="px-4 py-3 text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                        {t("portfolio.totalOutstanding")}
                      </td>
                      <td className="hidden sm:table-cell" />
                      <td className="hidden sm:table-cell" />
                      <td className="px-4 py-3 text-right font-bold tabular-nums text-negative">
                        {formatCurrency(totalLiabilitiesValue, portfolioCurrency)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      </div>

      <IncomeTermsModal
        open={incomeTermsItem !== null}
        onOpenChange={(open) => !open && setIncomeTermsItem(null)}
        mode="asset"
        assetId={incomeTermsItem?.id ?? null}
        assetLabel={incomeTermsItem?.symbol}
        currentValue={incomeTermsItem ? parseDecimal(incomeTermsItem.native?.marketValue) : undefined}
        // #83 R5: pre-ticks "apply to all holdings" when the symbol is held in
        // more than one account (the modal checks the server-side siblings; cash excluded).
        defaultApplyToSymbol
      />
    </div>
  );
}
